import { useEffect, useState, useRef } from 'react';

import { usePostHog } from 'posthog-js/react';

import {
  fetchAuthenticatedUser,
  fetchCompetencies,
  fetchQuestions,
  fetchProsperContent,
  populateDataDictionary,
  fetchAllCareerPathContent,
  fetchUserInfo,
  fetchMostRecentUserResponseSet,
  fetchFeatureFlagsData,
} from './useBootstrap.util';
import { isGuestSessionPath } from '../appRoutes';
import { useGetBookmarksQuery } from '../redux/bookmarks/bookmarks.api';
import { setEmulator } from '../redux/emulator/emulator';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useGetPageContextDescriptionsQuery } from '../redux/pageContextDescriptions/pageContextDescriptions.api';
import { useFetchUserProfileInfoQuery } from '../redux/profile/profile';
import { USER_PROFILE_FIELDS } from '../redux/profile/profile.constants';
import { logoutThunk } from '../redux/session/logoutThunk';
import { loadSessionStatuses } from '../redux/sessionStatuses/sessionStatuses';
import {
  activateSharedAccountSession,
  SHARED_SESSION_GRANT_ID_KEY,
  SHARED_SESSION_RESOURCE_TYPE_KEY,
} from '../redux/sharedSession/sharedAccountSession';
import { sharedSessionGrantsApi } from '../redux/sharedSession/sharedSessionGrants.api';
import { useGetTrackAssessmentStandardsQuery } from '../redux/trackAssessment/trackAssessment.api';
import { extractEmulatorClaim, handleEmulationUrl } from '../utils/emulation';
import { mintGuestSession } from '../utils/guestSession';
import {
  clearRequestedSandboxPersona,
  clearSandboxSession,
  IS_SANDBOX,
  isSandboxTokenRejected,
  mintSandboxSession,
  requestedSandboxPersona,
  SANDBOX_PERSONA,
  storedSandboxPersona,
} from '../utils/sandboxSession';
import { getBackrsToken, isGuestToken, isGuestTokenExpired } from '../utils/tokenHelpers';

export type UseBootstrapResult = Readonly<{
  isBootstrapped: boolean;
  isSandboxUnavailable: boolean;
}>;

export const useBootstrap = (): UseBootstrapResult => {
  const dispatch = useAppDispatch();
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [isSandboxUnavailable, setIsSandboxUnavailable] = useState(false);
  const postHog = usePostHog();

  // Track with a ref to avoid duplicate calls
  const isBootstrappingRef = useRef(false);

  const user = useAppSelector((state) => state.user.user);

  useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !user?.id,
  });

  useGetTrackAssessmentStandardsQuery(undefined, {
    skip: !user?.id,
  });

  useGetBookmarksQuery(undefined, {
    skip: !user?.id,
  });

  // Prefetch page context descriptions for Lubav — no auth needed, rarely changes
  useGetPageContextDescriptionsQuery(undefined);

  useEffect(() => {
    async function bootstrap() {
      if (isBootstrappingRef.current) return;

      isBootstrappingRef.current = true;

      const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      try {
        //-----------------------------------------------
        // PHASE 1: Check for emulation URL and process emulation claim
        //-----------------------------------------------

        if (url.startsWith('/emulation')) {
          handleEmulationUrl(url, () => {
            dispatch(logoutThunk({ postHog }));
          });
        }

        const emulatorClaim = extractEmulatorClaim(getBackrsToken());

        if (emulatorClaim.exists) {
          if (emulatorClaim.isValid && emulatorClaim.emulator) {
            dispatch(setEmulator(emulatorClaim.emulator));
          } else {
            dispatch(logoutThunk({ postHog }));
          }
        }

        //-----------------------------------------------
        // PHASE 1.5: Check for shared session grant ID in localStorage for account sharing
        //-----------------------------------------------
        const grantId = localStorage.getItem(SHARED_SESSION_GRANT_ID_KEY);
        const resourceType = localStorage.getItem(SHARED_SESSION_RESOURCE_TYPE_KEY);

        if (grantId && resourceType === 'account') {
          const accessToken = getBackrsToken();

          if (accessToken) {
            try {
              // exchange the grant ID for a share session token
              const result = await dispatch(
                sharedSessionGrantsApi.endpoints.exchangeSessionByGrant.initiate({ grantId }),
              ).unwrap();

              // activate the shared account session in Redux
              dispatch(
                activateSharedAccountSession({
                  token: result.share_session_token,
                  ownerFirstName: result.ownerFirstName,
                  ownerLastName: result.ownerLastName,
                  ownerProfileImageUrl: result.ownerProfileImageUrl,
                }),
              );
            } catch (error) {
              console.error('Failed to exchange grant for session token:', error);
              localStorage.removeItem(SHARED_SESSION_GRANT_ID_KEY);
              localStorage.removeItem(SHARED_SESSION_RESOURCE_TYPE_KEY);
            }
          }
        }

        //-----------------------------------------------
        // PHASE 2: Fetch authtenticated user
        //-----------------------------------------------
        let token = getBackrsToken();

        const isExpiredGuest = isGuestToken(token) && isGuestTokenExpired(token);

        if (isExpiredGuest) token = null;

        // A sandbox visitor is always signed in as one of their own copy's coaches, never a guest.
        if (IS_SANDBOX) {
          const requestedPersona = requestedSandboxPersona(window.location.search);
          const storedPersona = storedSandboxPersona();
          // An unrecorded session must not read as the coach here, or a link naming the coach
          // would look like the account the visitor is already in and be dropped.
          const wantsOtherAccount = requestedPersona !== null && requestedPersona !== storedPersona;

          if (!token || wantsOtherAccount) {
            const persona = requestedPersona ?? storedPersona ?? SANDBOX_PERSONA.coach;
            let minted = await mintSandboxSession(persona);

            // An arrival the sandbox cannot serve the asked-for account falls back to the demo
            // coach: a first visit has no session to keep, and a dead end shows nothing at all.
            if (!minted && !token && persona !== SANDBOX_PERSONA.coach) {
              minted = await mintSandboxSession(SANDBOX_PERSONA.coach);
            }

            // A switch the sandbox cannot serve leaves the visitor in the session they arrived
            // with; only an arrival left with no session at all has nothing to show.
            if (!minted && !token) {
              setIsSandboxUnavailable(true);
              setIsBootstrapped(true);

              return;
            }

            if (minted) token = minted;
            else clearRequestedSandboxPersona();
          }
        }

        if (!token && !IS_SANDBOX && isGuestSessionPath(window.location.pathname)) {
          token = await mintGuestSession();
        }

        let currentUser =
          token && !isGuestToken(token) ? await fetchAuthenticatedUser(dispatch) : null;

        // Auto-login resolves to no user once the nightly rebuild has deleted the account this
        // token names. Replace the session rather than rendering a signed-in shell with no user.
        // A null user on its own also covers an API that could not answer, so confirm the rejection.
        if (IS_SANDBOX && token && !currentUser && (await isSandboxTokenRejected(token))) {
          const persona = clearSandboxSession() ?? SANDBOX_PERSONA.coach;

          token = await mintSandboxSession(persona);

          if (!token) {
            setIsSandboxUnavailable(true);
            setIsBootstrapped(true);

            return;
          }

          currentUser = await fetchAuthenticatedUser(dispatch);
        }

        // App is now ready for basic interaction
        setIsBootstrapped(true);

        //-----------------------------------------------
        // PHASE 3: Background Data Loading
        //-----------------------------------------------
        const competenciesPromise = fetchCompetencies(dispatch);
        const questionsPromise = fetchQuestions(dispatch);
        const careerPathContentPromise = fetchAllCareerPathContent(dispatch);

        fetchFeatureFlagsData(dispatch);

        competenciesPromise.then((competencies) => {
          if (competencies) {
            populateDataDictionary(dispatch, competencies);
          }
        });

        //-----------------------------------------------
        // PHASE 4: User-Specific Content (runs in background)
        //-----------------------------------------------
        if (currentUser) {
          fetchProsperContent(dispatch);

          // Warm structured-session statuses so the switch prompt resolves instantly later.
          dispatch(loadSessionStatuses());

          Promise.all([questionsPromise, careerPathContentPromise])
            .then(([questions, careerPathContent]) => {
              fetchUserInfo({ dispatch, questions, careerPathContent });
            })
            .catch((error) => {
              console.error('Failed to load user info dependencies', error);
            });
        } else {
          // Guest visitors can have a persisted blueprint too; load it so home routing knows it exists.
          Promise.all([questionsPromise, careerPathContentPromise])
            .then(([questions, careerPathContent]) => {
              fetchMostRecentUserResponseSet(dispatch, questions, careerPathContent);
            })
            .catch((error) => {
              console.error('Failed to load guest blueprint dependencies', error);
            });
        }
      } catch (error) {
        console.error('Failed to bootstrap app', error);
        setIsBootstrapped(false);
        isBootstrappingRef.current = false;
      } finally {
        isBootstrappingRef.current = false;
      }
    }

    // Start the bootstrap process
    bootstrap();
  }, [dispatch, postHog, user?.id]);

  return {
    isBootstrapped,
    isSandboxUnavailable,
  };
};
